import { _, api, config, helpers, ts } from '_core';

/**
 * Formats columns for upload by adding Finsera-specific columns and mapping values
 * @param columns - Object containing column name key-value pairs
 * @returns Object with original columns plus Finsera columns mapped appropriately
 */
export const _formatUploadColumns = (columns: Record<string, string>) => {
  const finseraCols = ['finsera_cusip', 'finsera_country', 'finsera_isin'];

  const localColumns = {
    finsera_fid: 'finsera_fid',
    finsera_ticker: 'finsera_ticker',
  } as Record<string, string>;

  localColumns['finsera_name'] = 'finsera_name';

  Object.keys(columns).forEach((key) => {
    if (columns[key] !== 'None') {
      localColumns[key] = columns[key];
      const finseraColFound = finseraCols.find((fc) => fc.includes(key));
      if (finseraColFound) localColumns[finseraColFound] = finseraColFound;
    }
  });

  return localColumns;
};
type BacktestApiResponse = {
  data: ts.types.backtest.Backtest;
};

export const createBacktest = async (
  newData: ts.types.backtest.BacktestDraft,
  callback: (_data: ts.types.backtest.Backtest) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    const resp: BacktestApiResponse = await api.backtests.create(newData);
    callback(resp.data);
  } catch (error) {
    errorCallback(`Error creating backtest - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};

export const updateBacktest = async (
  id: number,
  newData: ts.types.backtest.BacktestDraft,
  callback: (_data: ts.types.backtest.Backtest) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    const resp: BacktestApiResponse = await api.backtests.update(id, { ...newData });
    callback(resp.data);
  } catch (error) {
    errorCallback(`Error updating backtest - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};

export const updateDefinition = async (
  id: number,
  definition: ts.types.backtest.DefinitionDraft,
  isValid: boolean,
  callback: (_data: ts.types.backtest.Backtest) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    const localDef = _.pickByNestedObj<ts.types.backtest.DefinitionDraft>(definition, (x: any) => !_.isUndefined(x));
    const resp = await api.backtests.update(
      id,
      {
        definition: localDef,
        is_valid: isValid,
        skip_signature_check: !isValid,
      },
      true
    );

    callback(resp.data);
  } catch (error) {
    errorCallback(`Error updating the definition - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};

export const getMappedData = async (
  backtest: ts.types.backtest.Backtest,
  uiMetadata: ts.types.backtest.Backtest['ui_metadata'],
  callback: (_data: ts.types.rebalance.HoldingsData, _cols: { title: string; field: string }[]) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    const resp = await api.uiApi.getRebalanceData({
      data: {
        mapped_file: (backtest.mapped_file || '').replace(`backtests/${backtest.id}/`, ''),
        unmapped_file: (backtest.unmapped_file || '').replace(`backtests/${backtest.id}/`, ''),
        columns: Object.keys(_formatUploadColumns(uiMetadata.mapped_columns)),
        resource_id: backtest.id,
        ignore_cache: config.debug,
        resource: 'backtests',
      },
    });

    const data = _.isEmpty(resp?.data) ? [] : resp?.data;
    const cols = (resp?.columns as string[])
      .filter((x) => x !== 'error_message')
      .map((col) => ({
        title: _.startCase(col.replace('finsera', 'mapped')),
        field: col,
      }));

    callback(data, cols);
  } catch (error) {
    errorCallback(`Error getting data - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};
